/**
 * Current user + role context for the whole portal.
 *
 * Until this existed there was no way for a component to answer "who am I and
 * what am I allowed to see". roles.js defined the vocabulary, but Dashboard.jsx
 * never imported it and every user saw the identical page. Role-specific
 * dashboards and the consultant's My Day view both depend on this.
 *
 * Two sources, in order:
 *   1. Keycloak token claims (lib/auth.jsx) – identity and roles, available
 *      immediately with no network round-trip, so the first paint is already
 *      the right dashboard rather than a flash of the wrong one.
 *   2. GET /v1/users/me (usersService.me) – the authoritative record. Roles
 *      from the backend win over the token if they disagree, because the
 *      backend is what actually enforces them.
 *
 * Design-only mode (no Keycloak env vars – see lib/auth.jsx) still needs to
 * render something, so we fall back to a role held in localStorage under
 * `spectraiq.devRole`. That is a dev affordance, ignored the moment a real
 * token or a real /v1/users/me response is present.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useKeycloak } from './auth.jsx';
import { setOrgTimezone } from './datetime.js';
import usersService from './services/users.js';
import { can as canWithRoles, ROLE_GROUPS } from './roles.js';

const DEV_ROLE_KEY = 'spectraiq.devRole';

/**
 * Role precedence for choosing which dashboard to render. A user holding
 * several roles gets the most privileged one; a DP or super-admin can switch
 * down from there (see pages/Dashboard.jsx).
 */
export const ROLE_PRECEDENCE = [
  'SUPER_ADMIN',
  'CEO',
  'GM_SALES',   // "GM – Sales" in the client's revised chart
  'GM_SERVICE',        // "GM – Service" – peer of GM – Sales
  'DEALER_PRINCIPAL',
  'SALES_MANAGER',
  // Used Car Manager sits alongside Sales Manager (per client's revised chart
  // 2026-09-30) but ranks below it in precedence – a user holding both would
  // render the Sales Manager dashboard (broader scope: new + fleet + used).
  'USED_CAR_MANAGER',
  'SERVICE_MANAGER',
  'INVENTORY_MANAGER',        // "Inventory Controller" in the client's chart
  'TEST_DRIVE_CONTROLLER',    // Support-pod owner of Test Drives
  'BDC_AGENT',
  'FLEET_MANAGER',            // "Fleet Consultant" in the client's chart
  'NEW_CAR_SALES_CONSULTANT',
  'USED_CAR_SALES_CONSULTANT',
  'SALES_CONSULTANT',
  'SERVICE_ADVISOR',
  'READ_ONLY_AUDITOR',
];

/** Highest-privilege role held, or SALES_CONSULTANT when we know nothing. */
export function primaryRole(roles = []) {
  const held = new Set(roles || []);
  return ROLE_PRECEDENCE.find((r) => held.has(r)) || 'SALES_CONSULTANT';
}

/**
 * Roles that see dealership-wide data rather than only their own.
 *
 * Derived from ROLE_GROUPS.MANAGEMENT rather than named here – this function
 * held its own copy of the management roles and silently missed General
 * Manager and CEO when they were added, the same failure that emptied the
 * dashboard funnel for both. Checks the roles actually held, not just the
 * primary one, so a GM who also carries a floor role stays dealership-wide.
 */
export function isManagerial(roles = []) {
  return (roles || []).some((r) => ROLE_GROUPS.MANAGEMENT.includes(r));
}

/**
 * Only these may view another role's dashboard (used by the role switcher).
 * Dealer Principals see only their own dashboard; the switcher is a PMDG
 * operations tool, so it is limited to SUPER_ADMIN.
 */
export function canSwitchRole(roles = []) {
  return (roles || []).includes('SUPER_ADMIN');
}

const KNOWN_ROLES = new Set(ROLE_PRECEDENCE);

/** Normalise however a role arrives – keycloak lowercases, we don't. */
function normaliseRoles(input) {
  return [...new Set(input || [])]
    .map((r) => String(r).toUpperCase().replace(/-/g, '_'))
    .filter((r) => KNOWN_ROLES.has(r));
}

/**
 * Pull identity + roles out of a decoded Keycloak token. Roles live in two
 * places depending on how the realm maps them, so read both and merge.
 */
function fromToken(kc) {
  const t = kc?.tokenParsed;
  if (!t) return null;
  const realmRoles = t.realm_access?.roles || [];
  const clientId = kc.clientId || import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
  const clientRoles = clientId ? (t.resource_access?.[clientId]?.roles || []) : [];
  return {
    id: t.sub || null,
    name: t.name || t.preferred_username || null,
    email: t.email || null,
    roles: normaliseRoles([...realmRoles, ...clientRoles]),
  };
}

/** UserDTO → the shape this context exposes. */
function fromUserDto(u) {
  if (!u) return null;
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ')
    || u.name || u.email || null;
  return {
    id: u.id ?? null,
    name,
    email: u.email ?? null,
    roles: normaliseRoles(u.roles),
    dealershipId: u.dealershipId ?? null,
    // Not on UserDTO today. When the backend adds them (see the E10 backend
    // notes) they flow straight through; until then a single-rooftop dealer
    // simply has no location filter, which is the correct behaviour anyway.
    locationIds: u.locationIds ?? [],
    primaryLocationId: u.primaryLocationId ?? null,
    // On current deployments a consultant's user id IS their consultant id.
    // Kept as a distinct field so the day the backend separates them, only
    // this line changes.
    consultantId: u.consultantId ?? u.id ?? null,
    timezone: u.timezone ?? null,
    status: u.status ?? null,
  };
}

const CurrentUserContext = createContext(null);

export function CurrentUserProvider({ children }) {
  const kc = useKeycloak();
  const [user, setUser] = useState(() => fromToken(kc));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tokenUser = fromToken(kc);
    if (tokenUser) setUser((prev) => ({ ...(prev || {}), ...tokenUser }));

    usersService.me()
      .then((dto) => {
        if (cancelled) return;
        const me = fromUserDto(dto);
        if (!me) return;
        setUser((prev) => ({
          ...(prev || {}),
          ...me,
          // Backend roles are authoritative – it is what enforces them.
          roles: me.roles.length ? me.roles : (prev?.roles || []),
          name: me.name || prev?.name || null,
        }));
        // Only if the backend actually tells us. CrmProvider also sets the
        // org zone from Settings; whichever arrives first is correct and the
        // two agree, so this is a safe belt-and-braces.
        if (me.timezone) setOrgTimezone(me.timezone);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        // No backend and no token: design-only mode. Keep the app usable
        // rather than rendering an empty shell nobody can debug.
        if (!tokenUser) {
          let devRole = null;
          try { devRole = window.localStorage.getItem(DEV_ROLE_KEY); } catch { /* private mode */ }
          setUser((prev) => prev || {
            id: null,
            name: 'Design mode',
            email: null,
            roles: [devRole || 'DEALER_PRINCIPAL'],
            dealershipId: null,
            consultantId: null,
            locationIds: [],
            _designOnly: true,
          });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // kc is a module-level singleton; it does not change identity after init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    roles: user?.roles || [],
    role: primaryRole(user?.roles),
    consultantId: user?.consultantId || user?.id || null,
    dealershipId: user?.dealershipId || null,
    locationIds: user?.locationIds || [],
    isManagerial: isManagerial(user?.roles),
    canSwitchRole: canSwitchRole(user?.roles),
    designOnly: !!user?._designOnly,
    /**
     * Capability check, e.g. `can('settings.edit')`. See lib/roles.js.
     *
     * Exposed on the context rather than imported per component so a page
     * asks "may I?" without also having to know how roles are fetched, and
     * so the signed-out fallback below can answer honestly.
     */
    can: (capability) => canWithRoles(user?.roles || [], capability),
  }), [user, loading, error]);

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

/**
 * Never throws when the provider is absent – a page rendered in isolation
 * (a test, a design harness) gets a sane signed-out shape instead of a crash.
 */
export function useCurrentUser() {
  return useContext(CurrentUserContext) || {
    user: null,
    loading: false,
    error: null,
    roles: [],
    role: 'SALES_CONSULTANT',
    consultantId: null,
    dealershipId: null,
    locationIds: [],
    isManagerial: false,
    canSwitchRole: false,
    designOnly: false,
    // Fails closed. A component rendered outside the provider holds no roles,
    // so the honest answer to "may I?" is no – never a permissive default that
    // silently grants everything in a test or a design harness.
    can: () => false,
  };
}

/** Dev-only helper for the design-mode role switcher. */
export function setDevRole(role) {
  try { window.localStorage.setItem(DEV_ROLE_KEY, role); } catch { /* ignore */ }
}
